import React from "react";
import { useAuth } from "../context/AuthContext";

import Header from "../components/Header";
import Footer from "../components/Footer";

import logo from "../assets/images/logo.png";
import heroBackground from "../assets/images/hero-background.png";

export default function Privacy() {
  const { user } = useAuth();

  return (
    <div
      className="relative min-h-screen overflow-x-hidden bg-[#050613] text-white bg-no-repeat bg-contain bg-center-top"
      style={{
        backgroundImage: `url(${heroBackground})`,
      }}
    >
      <div className="pointer-events-none absolute inset-0" />
      <Header user={user} logo={logo} />

      <main className="px-4 pt-10 pb-32 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-4xl animate-fadeInSoft" style={{ animationDelay: "80ms" }}>
            <div className="mb-8 text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs tracking-widest text-white/80 backdrop-blur">
                + PRIVACY
              </span>
              <h1 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-medium text-white/90">
                Privacy Policy
              </h1>
            </div>

            <div className="space-y-7 text-sm leading-relaxed text-white/80">
              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Introduction</h2>
                <p>
                  This Privacy Policy explains how MyRDP (&quot;MyRDP&quot;, &quot;we&quot;, &quot;us&quot;,
                  &quot;our&quot;) collects, processes, and safeguards personal information in connection with the use of
                  our website and RDP/VPS hosting services.
                </p>
                <p>
                  We are committed to ensuring that personal data is handled in a secure, lawful, and transparent manner.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Information We Collect</h2>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-white/90">Account Data</h3>
                  <p>
                    When you register or maintain an account, we may collect information such as your email address,
                    username, and any optional contact details you choose to provide. MyRDP does not collect or store
                    traditional billing information such as physical billing addresses, bank account details,
                    credit/debit card information, or PayPal credentials.
                  </p>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-white/90">Technical and Usage Data</h3>
                  <p>
                    We may automatically collect certain technical information, including IP address, browser type,
                    login activity, timestamps, and service usage patterns.
                  </p>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-white/90">Payment Information (Cryptocurrency Only)</h3>
                  <p>
                    MyRDP currently supports payments exclusively via cryptocurrency through an authorized third-party
                    payment processor. We do not handle or store credit card or PayPal information. Limited
                    transaction-related metadata (such as payment status or reference identifiers) may be processed solely
                    for account balance management. We do not have access to or store private wallet keys or complete
                    payment credentials.
                  </p>
                </div>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Purpose of Data Processing</h2>
                <p>We process personal data for the following purposes:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Account registration and service provisioning</li>
                  <li>Payment processing and account balance management</li>
                  <li>Customer support and communication</li>
                  <li>Monitoring service performance and preventing abuse</li>
                  <li>Compliance with applicable legal and regulatory obligations</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Data Security Measures</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <span className="font-semibold text-white/90">Secure Transmission</span>: All data exchanged between
                    users and our systems is protected through encrypted TLS/SSL connections.
                  </li>
                  <li>
                    <span className="font-semibold text-white/90">Access Management</span>: Access to personal data is
                    strictly limited to authorized personnel based on operational requirements.
                  </li>
                  <li>
                    <span className="font-semibold text-white/90">Infrastructure Protection</span>: Our systems are
                    secured through industry-standard practices including network monitoring, firewalls, and intrusion
                    detection/prevention mechanisms.
                  </li>
                  <li>
                    <span className="font-semibold text-white/90">DDoS Protection</span>: We implement network-level
                    safeguards to mitigate denial-of-service attacks and maintain service availability.
                  </li>
                  <li>
                    <span className="font-semibold text-white/90">Payment Handling</span>: Cryptocurrency transactions
                    are processed through our external payment provider under their respective security standards. MyRDP
                    does not process or store traditional payment data.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Disclosure of Information</h2>
                <p>MyRDP does not sell, rent, or trade personal data.</p>
                <p>Information may be shared only where necessary with:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Authorized cryptocurrency payment processors (for transaction verification)</li>
                  <li>Infrastructure and hosting providers</li>
                  <li>Security and fraud prevention partners</li>
                  <li>Competent authorities where required by law or legal process</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">User Rights</h2>
                <p>Depending on applicable law, you may have the right to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Access and review your personal data</li>
                  <li>Request corrections or updates</li>
                  <li>Request deletion of data, where legally permissible</li>
                  <li>Restrict or object to certain processing activities</li>
                  <li>Withdraw consent for non-essential communications</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Cookies and Tracking Technologies</h2>
                <p>
                  We utilize cookies and similar technologies to improve functionality, analyze usage, and enhance user
                  experience.
                </p>
                <p>You may control or disable cookies through your browser settings.</p>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Data Retention</h2>
                <p>Personal data is retained only for as long as necessary to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Provide and maintain Services</li>
                  <li>Fulfill legal and regulatory obligations</li>
                  <li>Resolve disputes and enforce agreements</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">International Data Transfers</h2>
                <p>
                  Your information may be processed in multiple jurisdictions, including but not limited to the United
                  States and the European Union, depending on infrastructure and service providers.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Legal Disclosure</h2>
                <p>We may disclose personal data when required to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Comply with applicable laws or legal processes</li>
                  <li>Investigate or prevent fraud and abuse</li>
                  <li>Protect the rights, security, and integrity of MyRDP, its users, or third parties</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Policy Updates</h2>
                <p>
                  MyRDP reserves the right to amend this Privacy Policy at any time. Updates will be published on this
                  page along with the revised effective date.
                </p>
                <p className="text-white/70">Last Updated: 2026-03-27</p>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Contact Information</h2>
                <p>For any questions or requests related to this Privacy Policy, please contact us at:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    Email:{" "}
                    <a href="mailto:support@myrdp.io" className="text-[#486FFF] underline underline-offset-2 hover:text-[#7c9cff]">
                      support@myrdp.io
                    </a>
                  </li>
                  <li>Company: MyRDP</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer user={user} />
    </div>
  );
}
